"use client";

import { c as _c } from "react/compiler-runtime";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useCUUsageByWorkflow } from "@/hooks/useCU";
import Link from "next/link";
import { useMemo, useState } from "react";
interface WorkflowCostBreakdownProps {
  organizationId?: string;
  from?: number;
  to?: number;
}
type StatusFilter = "all" | "active" | "paused";
function TriggerBadge(t0) {
  const $ = _c(4);
  const {
    triggerType,
    isStream
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      cronjob: "Cronjob",
      schedule: "Schedule",
      webhook: "Webhook",
      manual: "Manual"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const labels = t1;
  if (isStream) {
    let t2;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <Badge variant="cyan" dot={true} dotPulse={true}>Stream</Badge>;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    return t2;
  }
  const t2 = labels[triggerType] || triggerType;
  let t3;
  if ($[2] !== t2) {
    t3 = <Badge variant="default">{t2}</Badge>;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}
function StatusBadge(t0) {
  const $ = _c(2);
  const {
    status
  } = t0;
  if (status === "active") {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <Badge variant="success" dot={true}>Active</Badge>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Badge variant="warning" dot={true}>Paused</Badge>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function formatCU(value: string): string {
  const num = parseFloat(value);
  if (num >= 1000) return `${(num / 1000).toFixed(1)}k`;
  if (num >= 1) return num.toFixed(0);
  return num.toFixed(2);
}
const STATUS_FILTERS: {
  value: StatusFilter;
  label: string;
}[] = [{
  value: "all",
  label: "All"
}, {
  value: "active",
  label: "Active"
}, {
  value: "paused",
  label: "Paused"
}];
export function WorkflowCostBreakdown(t0) {
  const $ = _c(19);
  const {
    organizationId,
    from,
    to
  } = t0;
  const {
    data,
    isLoading
  } = useCUUsageByWorkflow(organizationId, from, to);
  const [statusFilter, setStatusFilter] = useState("all");
  data?.workflows;
  let t1;
  bb0: {
    let t2;
    if ($[0] !== data?.workflows) {
      t2 = data?.workflows ?? [];
      $[0] = data?.workflows;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    const all = t2;
    if (statusFilter === "all") {
      t1 = all;
      break bb0;
    }
    let t3;
    if ($[2] !== all || $[3] !== statusFilter) {
      let t4;
      if ($[5] !== statusFilter) {
        t4 = wf => wf.status === statusFilter;
        $[5] = statusFilter;
        $[6] = t4;
      } else {
        t4 = $[6];
      }
      t3 = all.filter(t4);
      $[2] = all;
      $[3] = statusFilter;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    t1 = t3;
  }
  const workflows = t1;
  if (isLoading) {
    let t2;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <div className="space-y-3">{[1, 2, 3].map(_temp)}</div>;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    return t2;
  }
  const totalCU = workflows.reduce(_temp2, 0);
  let t2;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <h2 className="heading-lg">Cost by Workflow</h2>;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = value => setStatusFilter(value as StatusFilter);
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <TabsList>{STATUS_FILTERS.map(_temp3)}</TabsList>;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== statusFilter) {
    t5 = <div className="flex items-center justify-between">{t2}<Tabs value={statusFilter} onValueChange={t3} size="sm">{t4}</Tabs></div>;
    $[11] = statusFilter;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== totalCU || $[14] !== workflows) {
    t6 = workflows.length === 0 ? <p className="body-sm flex h-20 items-center justify-center">No workflow usage data for this period.</p> : <div className="border-ds-border-default overflow-hidden rounded-xl border"><Table><TableHeader><TableRow className="border-ds-border-default hover:bg-ds-surface-hover border-b bg-zinc-50 dark:bg-zinc-950 dark:hover:bg-zinc-950"><TableHead className="table-header">Workflow</TableHead><TableHead className="table-header">Type</TableHead><TableHead className="table-header">Status</TableHead><TableHead className="table-header text-right">Est. CU/run</TableHead><TableHead className="table-header text-right">Total CU</TableHead><TableHead className="table-header text-right">CU/hour</TableHead><TableHead className="table-header text-right">CU/day</TableHead><TableHead className="table-header text-right">Runs</TableHead><TableHead className="table-header text-right">Share</TableHead></TableRow></TableHeader><TableBody className="bg-ds-surface-card">{workflows.map(wf_0 => {
            const cuPerHour = parseFloat(wf_0.cuPerHour);
            const cuPerDay = cuPerHour * 24;
            const share = totalCU > 0 ? parseFloat(wf_0.totalCu) / totalCU * 100 : 0;
            return <TableRow key={wf_0.workflowId} className="border-ds-border-default dark:hover:bg-ds-neutral-800/50 hover:bg-ds-surface-hover transition-colors"><TableCell><Link href={`/workflows/edit?id=${wf_0.workflowId}`} className="text-ds-text-primary font-medium hover:text-blue-600 hover:underline dark:hover:text-blue-400">{wf_0.workflowName}</Link></TableCell><TableCell><TriggerBadge triggerType={wf_0.triggerType} isStream={wf_0.isStream} /></TableCell><TableCell><StatusBadge status={wf_0.status} /></TableCell><TableCell className="text-ds-text-muted text-right font-mono text-xs">{wf_0.estimatedCostPerRun > 0 ? wf_0.estimatedCostPerRun : "\u2014"}</TableCell><TableCell className="text-ds-text-primary text-right font-mono text-xs font-medium dark:text-zinc-200">{formatCU(wf_0.totalCu)}</TableCell><TableCell className="text-ds-text-muted text-right font-mono text-xs">{wf_0.isStream ? formatCU(wf_0.cuPerHour) : "\u2014"}</TableCell><TableCell className="text-ds-text-muted text-right font-mono text-xs">{wf_0.isStream ? formatCU(cuPerDay.toString()) : "\u2014"}</TableCell><TableCell className="text-ds-text-muted text-right font-mono text-xs">{wf_0.runCount}</TableCell><TableCell className="text-right"><div className="flex items-center justify-end gap-2"><div className="bg-ds-neutral-100 dark:bg-ds-neutral-800 h-1.5 w-16 overflow-hidden rounded-full"><div className="h-full rounded-full bg-blue-500/70" style={{
                      width: `${Math.min(share, 100)}%`
                    }} /></div><span className="text-ds-text-muted dark:text-ds-text-muted w-10 text-right font-mono text-xs">{share.toFixed(0)}%</span></div></TableCell></TableRow>;
          })}</TableBody></Table></div>;
    $[13] = totalCU;
    $[14] = workflows;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== t5 || $[17] !== t6) {
    t7 = <div className="flex flex-col gap-4">{t5}{t6}</div>;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  return t7;
}
function _temp3(f) {
  return <TabsTrigger key={f.value} value={f.value}>{f.label}</TabsTrigger>;
}
function _temp2(sum, w) {
  return sum + parseFloat(w.totalCu);
}
function _temp(i) {
  return <div key={i} className="bg-ds-neutral-100 dark:bg-ds-neutral-800/50 h-10 animate-pulse rounded-lg" />;
}